import { useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import Joi from "joi";
import { size } from "lodash";
import { format } from "date-fns";

import { Urls } from "../../../utils/constant";
import { useApi } from "../apiCall/useApi";
import useFullPageLoader from "../Common/PageLoader/useFullPageLoader";

import {
  hidePageLoader,
  showPageLoader,
} from "../../reducers/Common/PageLoader/PageLoader.reducer";

import {
    setDoctorComboList,
  setPatientComboList,
  setReviewData,
} from "../../reducers/Review/Review.reducer";

import {
    getDoctorList,
  getPatientList,
  getReviewData,
} from "../../selectors/Review/Review.selector";

export const reviewFormSchema = Joi.object({
  name: Joi.string().required().messages({
    "string.empty": "Doctor selection is required.",
  }),
  patientName: Joi.string().required().messages({
    "string.empty": "Patient selection is required.",
  }),
  comment: Joi.string().optional().allow("",null),

});

export function useReviewForm({
  existingDoctorId,
  existingPatientId,
  existingStatus,
  existingRating,
  existingComment,
  existingIsRecommend,
  existingDateTime,
}) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { apiPost } = useApi();
  const [loading, setLoading] = useState(false);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [Id, setId] = useState("");
  const [DoctorId, setDoctorId] = useState(existingDoctorId);
  const [PatientId, setPatientId] = useState(existingPatientId);
  const [status, setStatus] = useState(existingStatus === 2 ? 0 : 1);
  const [DateTime, setDateTime] = useState(existingDateTime ? new Date(existingDateTime) : new Date());
  const [rating, setRating] = useState(existingRating || 1);
  const [comment, setComment] = useState(existingComment || "");
  const [isRecommend, setIsRecommend] = useState(existingIsRecommend === 2 ? 0 : 1);

  // Selectors
  const reviewData = useSelector(getReviewData);
  const doctorList = useSelector(getDoctorList);
  const patientList = useSelector(getPatientList);

  const handleDeleteId = useCallback((row) => {
    setId(row.id);
    setDoctorId(row.doctor_id);
    setPatientId(row.patient_id);
    setStatus(row.status);
    setRating(row.rating);
    setComment(row.comment);
    setIsRecommend(row.is_recommend);
  }, []);

  // Update state when reviewData changes (when editing)
  useEffect(() => {
    if (reviewData) {
      setDoctorId(reviewData.doctor_id || "");
      setPatientId(reviewData.patient_id || "");
      setStatus(reviewData.status === 2 ? 0 : 1);
      setRating(reviewData.rating || 1);
      setComment(reviewData.comment || "");
      setIsRecommend(reviewData.is_recommend === 2 ? 0 : 1);
      if (reviewData.date_time) {
        setDateTime(new Date(reviewData.date_time));
      }
    }
  }, [reviewData]);

  // Get review by ID function
  const getReviewById = useCallback(
    async (id) => {
      if (loading) return;
      setLoading(true);
      try {
        dispatch(showPageLoader());
        const { data } = await apiPost({
          url: Urls.getReviewById(),
          data: {
            id: id,
          },
        });

        dispatch(setReviewData(data));
        setLoading(false);
        dispatch(hidePageLoader());
        return data;
      } catch (err) {
        dispatch(hidePageLoader());
        console.log(err);
        setLoading(false);
        enqueueSnackbar("Failed to fetch review data", {
          variant: "error",
        });
        return false;
      }
    },
    [loading, dispatch, apiPost, enqueueSnackbar]
  );

    // Update review function
  const updateReviewData = useCallback(
    async ({
      id
    }) => {
      if (loading) return;
      setLoading(true);
      const status_Val = status === false ? 2 : status === 0 ? 2 : 1;
      const isRecommend_Val = isRecommend === 0 ? 2 : 1;
       
      try {
        dispatch(showPageLoader());
        const formattedDateTime = format(DateTime, "yyyy-MM-dd HH:mm:ss");
         const { data } = await apiPost({
           url: Urls.updateReview(),
           data: {
             id: id,
             doctor_id: DoctorId,
             patient_id: PatientId,
             date_time: formattedDateTime,
             rating: rating,
             comment: comment,
             is_recommend: isRecommend_Val ,
             status: status_Val,
             update_by: ""
           },
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
        
        if (data && data.id && size(data.id) === 36) {
          enqueueSnackbar("Review updated successfully", {
            variant: "success",
          });
          setLoading(false);
          dispatch(hidePageLoader());
          return true;
        }

        setLoading(false);
        dispatch(hidePageLoader());
        return false;
      } catch (err) {
        dispatch(hidePageLoader());
        console.log(err);
        setLoading(false);
        enqueueSnackbar("Failed to update review", {
          variant: "error",
        });
        return false;
      }
    },
    [loading, status, dispatch, apiPost, enqueueSnackbar, DateTime, DoctorId, PatientId, rating, comment, isRecommend]
  );

   // Delete review function (soft delete - sets status to inactive)
   const deleteReviewData = useCallback(
    async () => {
      if (loading) return;
      setLoading(true);  
        
      try {
        dispatch(showPageLoader());
        const formattedDateTime = format(DateTime, "yyyy-MM-dd HH:mm:ss");
        const isRecommend_Val = isRecommend === 0 ? 2 : 1;
         const { data } = await apiPost({
           url: Urls.updateReview(),
           data: {
             id: Id,
             doctor_id: DoctorId,
             patient_id: PatientId,
             date_time: formattedDateTime,
             rating: rating,
             comment: comment,
             is_recommend: isRecommend_Val ,
             status: 2, // Status 2 = Inactive/Deleted
             update_by: ""
           },
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
        
        if (data && data.id && size(data.id) === 36) {
          enqueueSnackbar("Review deleted successfully", {
            variant: "success",
          });
          setLoading(false);
          dispatch(hidePageLoader());
          return true;
        }

        setLoading(false);
        dispatch(hidePageLoader());
        return false;
      } catch (err) {
        dispatch(hidePageLoader());
        console.log(err);
        setLoading(false);
        enqueueSnackbar("Failed to delete review", {
          variant: "error",
        });
        return false;
      }
    },
    [loading, status, dispatch, apiPost, enqueueSnackbar, DateTime, DoctorId, PatientId, rating, comment, isRecommend]
  );


  const fetchDoctorComboList = useCallback(async () => {
    try {
      const { data } = await apiPost({
        url: Urls.getDoctorCombo(),
        data: {},
      });
      // setting list to reducer

      dispatch(setDoctorComboList(data.data));
    } catch (err) {
      console.log(err);
    }
  }, [apiPost, dispatch]);

  const findDoctorFromLabel = useCallback(
    (value) => {
      const selectedDoctorId = value
        ? doctorList.find((item) => item.label === value)?.id
        : null;

      setDoctorId(selectedDoctorId);
      return selectedDoctorId;
    },
    [doctorList]
  );

  const fetchPatientComboList = useCallback(async () => {
    try {
      const { data } = await apiPost({
        url: Urls.getPatientCombo(),
        data: {},
      });
      // setting list to reducer

      dispatch(setPatientComboList(data.data));
    } catch (err) {
      console.log(err);
    }
  }, [apiPost, dispatch]);

  const findpatientFromLabel = useCallback(
    (value) => {
      const selectedPatientId = value
        ? patientList.find((item) => item.label === value)?.id
        : null;
      setPatientId(selectedPatientId);
      return selectedPatientId;
    },
    [patientList]
  );

  const handleStatusChange = useCallback((event) => {
    if (event.target.checked === 1) {
      setStatus(event.target.checked);
    } else {
      setStatus(event.target.checked);
    }
  }, []);

  const handleDateTime = useCallback((newDate) => {
    setDateTime(newDate);
  }, []);

  const handleRatingChange = useCallback((newRating) => {
    setRating(newRating);
  }, []);

  const handleCommentChange = useCallback((event) => {
    setComment(event.target.value);
  }, []);

  const handleRecommendationChange = useCallback((event) => {
    const newValue = event.target.checked ? 1 : 0;
    setIsRecommend(newValue);
  }, []);


  
  return {
    // State
    loading,
    loader,
    reviewData,
    DoctorId,
    PatientId,
    status,
    doctorList,
    patientList,
    DateTime,
    handleDateTime,
    rating,
    handleRatingChange,
    comment,
    handleCommentChange,
    isRecommend,
    handleRecommendationChange,
    handleDeleteId,
    deleteReviewData,
    // Schema
    reviewFormSchema,
    
    // Functions
    getReviewById,
    updateReviewData,
    fetchDoctorComboList,
    findDoctorFromLabel,
    fetchPatientComboList,
    findpatientFromLabel,
    handleStatusChange,
  };
}
